import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="onboarding--scroll-region"
export default class extends Controller {
  static values = {
    overflowTolerance: { type: Number, default: 24 }
  }

  connect() {
    this.isScrollable = false
    this.refreshFrame = null
    this.refresh = this.refresh.bind(this)
    this.scheduleRefresh = this.scheduleRefresh.bind(this)
    this.handleMutations = this.handleMutations.bind(this)

    this.element.scrollTop = 0
    this.scheduleRefresh()
    requestAnimationFrame(() => {
      this.element.scrollTop = 0
      this.scheduleRefresh()
    })
    window.addEventListener("resize", this.scheduleRefresh)

    if ("ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.scheduleRefresh)
      this.resizeObserver.observe(this.element)

      const body = this.element.closest(".ob-body")
      if (body) this.resizeObserver.observe(body)
      Array.from(this.element.children).forEach((child) => this.resizeObserver.observe(child))
    }

    if ("MutationObserver" in window) {
      this.mutationObserver = new MutationObserver(this.handleMutations)
      this.mutationObserver.observe(this.element, {
        attributes: true,
        attributeFilter: ["class", "style", "hidden", "aria-hidden"],
        childList: true,
        subtree: true
      })
    }
  }

  disconnect() {
    window.removeEventListener("resize", this.scheduleRefresh)
    if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame)
    this.resizeObserver?.disconnect()
    this.mutationObserver?.disconnect()
  }

  scheduleRefresh() {
    if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame)
    this.refreshFrame = requestAnimationFrame(() => {
      this.refreshFrame = null
      this.refresh()
    })
  }

  handleMutations(mutations) {
    const onlyOwnClassChange = mutations.every(
      (mutation) =>
        mutation.type === "attributes" && mutation.target === this.element && mutation.attributeName === "class"
    )

    if (onlyOwnClassChange) return

    this.scheduleRefresh()
  }

  refresh() {
    const isDesktop = window.matchMedia("(min-width: 1200px)").matches
    const overflowAmount = Math.ceil(this.contentBottom() - this.element.clientHeight)
    const isScrollable = isDesktop && overflowAmount > this.overflowToleranceValue

    if (isScrollable !== this.isScrollable) {
      this.element.classList.toggle("ob-section-scroll--scrollable", isScrollable)
      this.isScrollable = isScrollable
    }

    if (!isScrollable) {
      this.element.scrollTop = 0
    }
  }

  contentBottom() {
    const sectionRect = this.element.getBoundingClientRect()

    return Array.from(this.element.children).reduce((maxBottom, child) => {
      if (!(child instanceof HTMLElement)) return maxBottom
      if (child.getClientRects().length === 0) return maxBottom

      const childBottom = child.getBoundingClientRect().bottom - sectionRect.top
      return Math.max(maxBottom, childBottom)
    }, 0)
  }
}
